import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../utils/config/config";

const EMPTY_STATS = { total: 0, approved: 0, pending: 0, rejected: 0, archived: 0 };

export const useAdminPractitioners = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const notify = useCallback((severity, msg) => setSnack({ open: true, severity, msg }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await axiosInstance.get("practitioners/admin", { params });
      setItems(data.items || []);
      setStats(data.stats || EMPTY_STATS);
    } catch (err) {
      notify("error", err.response?.data?.message || "Failed to load practitioners");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, notify]);

  // Debounce search; refetch on filter change.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const moderate = useCallback(
    async (id, status) => {
      try {
        await axiosInstance.patch(`practitioners/${id}/moderate`, { status });
        notify("success", `Practitioner ${status}`);
        load();
      } catch (err) {
        notify("error", err.response?.data?.message || "Failed to update status");
      }
    },
    [notify, load]
  );

  const addPractitioner = useCallback(
    async (payload) => {
      // returns true on success so the caller can close its modal
      try {
        await axiosInstance.post("practitioners", payload);
        notify("success", "Practitioner created — a set-password email has been sent.");
        load();
        return true;
      } catch (err) {
        notify("error", err.response?.data?.message || "Failed to add practitioner");
        return false;
      }
    },
    [notify, load]
  );

  const publishEdit = useCallback(
    async (id) => {
      try {
        await axiosInstance.post(`practitioners/${id}/pending-edit/publish`);
        notify("success", "Changes published");
        load();
        return true;
      } catch (err) {
        notify("error", err.response?.data?.message || "Failed to publish changes");
        return false;
      }
    },
    [notify, load]
  );

  const rejectEdit = useCallback(
    async (id, comment) => {
      try {
        await axiosInstance.post(`practitioners/${id}/pending-edit/reject`, { comment });
        notify("success", "Changes rejected — the practitioner has been notified.");
        load();
        return true;
      } catch (err) {
        notify("error", err.response?.data?.message || "Failed to reject changes");
        return false;
      }
    },
    [notify, load]
  );

  // Archiving disables the login and hides the profile; the record is kept so
  // payouts and payment history stay attributable. Reversible via restore.
  // (The endpoint is DELETE — the backend calls the same thing "archive".)
  const archivePractitioner = useCallback(
    async (id) => {
      try {
        await axiosInstance.delete(`practitioners/${id}`);
        notify("success", "Practitioner archived — the account is disabled and hidden.");
        load();
        return true;
      } catch (err) {
        notify("error", err.response?.data?.message || "Failed to archive practitioner");
        return false;
      }
    },
    [notify, load]
  );

  const restorePractitioner = useCallback(
    async (id) => {
      try {
        await axiosInstance.post(`practitioners/${id}/restore`);
        notify("success", "Practitioner restored");
        load();
        return true;
      } catch (err) {
        notify("error", err.response?.data?.message || "Failed to restore practitioner");
        return false;
      }
    },
    [notify, load]
  );

  const closeSnack = useCallback(() => setSnack((s) => ({ ...s, open: false })), []);

  return {
    loading,
    items,
    stats,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    moderate,
    addPractitioner,
    publishEdit,
    rejectEdit,
    archivePractitioner,
    restorePractitioner,
    reload: load,
    snack,
    closeSnack,
  };
};
